import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  alpha,
  IconButton,
  FormGroup,
  Switch,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  VideoLibrary as VideoIcon,
  Slideshow as ShortsIcon,
  Image as PhotoIcon,
  OndemandVideo as ReelIcon,
  Article as PostIcon,
  AttachFile as FileIcon,
  ErrorOutline as ErrorOutlineIcon,
  MusicOff as MusicOffIcon,
  AutoAwesome as MagicIcon,
} from '@mui/icons-material';
import { PLATFORM_CONFIG, PLATFORM_ICONS } from './PLATFORM_CONFIG';
import PlatformMediaPicker from './PlatformMediaPicker';
import AiThumbnailGenerator from './AiThumbnailGenerator';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

// ─── Platform character limits ────────────────────────────────────────────────

const LIMITS = {
  youtube: { title: 100, description: 5000, tags: 500 },
  instagram: { text: 2200 },
  facebook: { text: 10000 },
  twitter: { text: 280 },
  linkedin: { text: 3000 },
  reddit: { subreddit: 21, title: 300, text: 40000 },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Returns a FormHelperText-compatible node with a right-aligned character counter.
 * Turns orange at 80 %, red at 95 %.
 */
function counterHelper(current = 0, max, leftNote) {
  const pct = current / max;
  const counterColor =
    pct >= 0.95 ? 'error.main' : pct >= 0.80 ? 'warning.main' : 'text.disabled';

  return (
    <Box
      component="span"
      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
    >
      <span>{leftNote ?? ' '}</span>
      <Typography
        component="span"
        variant="caption"
        sx={{ color: counterColor, fontWeight: pct >= 0.90 ? 700 : 400 }}
      >
        {current.toLocaleString()} / {max.toLocaleString()}
      </Typography>
    </Box>
  );
}

// ─── Shared content-type toggle ───────────────────────────────────────────────

function ContentTypeToggle({ options, value, onChange }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
        Content Type
      </Typography>
      <ToggleButtonGroup
        value={value || options[0].value}
        exclusive
        size="small"
        onChange={(_, val) => { if (val) onChange('content_type', val); }}
      >
        {options.map((opt) => (
          <ToggleButton key={opt.value} value={opt.value} sx={{ px: 2.5, fontWeight: 600, gap: 0.75 }}>
            {opt.icon}
            {opt.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
}

// ─── Per-platform field sets ──────────────────────────────────────────────────

// ─── YouTube category list (subset of official YouTube categories) ────────────
const YOUTUBE_CATEGORIES = [
  { id: '22', label: 'People & Blogs' },
  { id: '28', label: 'Science & Technology' },
  { id: '27', label: 'Education' },
  { id: '24', label: 'Entertainment' },
  { id: '10', label: 'Music' },
  { id: '20', label: 'Gaming' },
  { id: '17', label: 'Sports' },
  { id: '26', label: 'Howto & Style' },
  { id: '25', label: 'News & Politics' },
  { id: '1', label: 'Film & Animation' },
  { id: '2', label: 'Autos & Vehicles' },
  { id: '23', label: 'Comedy' },
  { id: '19', label: 'Travel & Events' },
  { id: '15', label: 'Pets & Animals' },
];

function YouTubeFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.youtube;
  const titleLen = (customization.title || '').length;
  const descLen = (customization.description || '').length;
  const tagsArr = Array.isArray(customization.tags) ? customization.tags : [];
  const tagsLen = tagsArr.join('').length; // approximate character sum for limit

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ContentTypeToggle
        options={[
          { value: 'video', label: 'Long-form Video', icon: <VideoIcon fontSize="small" /> },
          { value: 'short', label: 'Short (≤ 60 s)', icon: <ShortsIcon fontSize="small" /> },
        ]}
        value={customization.content_type}
        onChange={onChange}
      />

      <TextField
        fullWidth
        label="Video Title"
        placeholder="Enter a compelling title for your video"
        value={customization.title || ''}
        onChange={(e) => onChange('title', e.target.value.slice(0, lim.title))}
        error={showErrors && !!errors.youtube_title}
        required
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(titleLen, lim.title, showErrors ? errors.youtube_title : undefined)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        multiline
        minRows={9}
        maxRows={24}
        label="Description"
        placeholder="Describe your video — what it covers, chapters, links, credits…"
        value={customization.description || ''}
        onChange={(e) => onChange('description', e.target.value.slice(0, lim.description))}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          descLen,
          lim.description,
          customization.content_type === 'short' ? '#Shorts appended automatically' : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagsArr}
        onChange={(event, newValue) => {
          // Limit total characters and number of tags (YouTube limit is ~500 chars total)
          const newTags = newValue.map(t => t.trim()).filter(Boolean);
          if (newTags.join('').length <= lim.tags) {
            onChange('tags', newTags);
          }
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip
                key={key}
                variant="outlined"
                label={option}
                size="small"
                {...tagProps}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Tags"
            placeholder={tagsArr.length === 0 ? "tech, tutorial, shorts (Press Enter)" : ""}
            FormHelperTextProps={{ component: 'div' }}
            helperText={counterHelper(tagsLen, lim.tags, 'Press Enter to create a tag')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onAskAI} title="Suggest tags with AI">
                      <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {/* Category */}
      <FormControl fullWidth>
        <InputLabel>Category</InputLabel>
        <Select
          value={customization.category_id || '22'}
          label="Category"
          onChange={(e) => onChange('category_id', e.target.value)}
        >
          {YOUTUBE_CATEGORIES.map((cat) => (
            <MenuItem key={cat.id} value={cat.id}>{cat.label}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Privacy Status */}
      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
          Privacy
        </Typography>
        <ToggleButtonGroup
          value={customization.privacy_status || 'public'}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) onChange('privacy_status', val); }}
        >
          <ToggleButton value="public" sx={{ px: 2.5, fontWeight: 600 }}>Public</ToggleButton>
          <ToggleButton value="unlisted" sx={{ px: 2.5, fontWeight: 600 }}>Unlisted</ToggleButton>
          <ToggleButton value="private" sx={{ px: 2.5, fontWeight: 600 }}>Private</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Thumbnail */}
      <Box>
        <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
          Custom Thumbnail
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 1 }}>
          JPEG or PNG, recommended 1280×720, max 2 MB
        </Typography>
        <PlatformMediaPicker
          value={customization.thumbnail_file || null}
          onChange={(file) => onChange('thumbnail_file', file)}
          accept="image/jpeg,image/png"
          label={null}
          compact
        />
        <AiThumbnailGenerator
          platform="youtube"
          onThumbnailReady={(file) => onChange('thumbnail_file', file)}
        />
      </Box>
    </Box>
  );
}

function InstagramFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.instagram;
  const textLen = (customization.text || '').length;
  const tagsArr = Array.isArray(customization.hashtags) ? customization.hashtags : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ContentTypeToggle
        options={[
          { value: 'post', label: 'Photo Post', icon: <PhotoIcon fontSize="small" /> },
          { value: 'reel', label: 'Reel (Video)', icon: <ReelIcon fontSize="small" /> },
        ]}
        value={customization.content_type}
        onChange={onChange}
      />

      <TextField
        fullWidth
        multiline
        minRows={7}
        maxRows={24}
        label="Caption"
        placeholder="Write your caption — use emojis, hashtags, @mentions to boost reach…"
        value={customization.text || ''}
        onChange={(e) => onChange('text', e.target.value.slice(0, lim.text))}
        error={showErrors && !!errors.instagram_text}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          textLen,
          lim.text,
          showErrors && errors.instagram_text ? errors.instagram_text : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagsArr}
        onChange={(event, newValue) => {
          const newTags = newValue.map(t => {
            let tag = t.trim();
            if (tag && !tag.startsWith('#')) return '#' + tag;
            return tag;
          }).filter(Boolean);
          onChange('hashtags', newTags);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} variant="outlined" label={option} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hashtags"
            placeholder={tagsArr.length === 0 ? "#viral, #foryou (Press Enter)" : ""}
            FormHelperTextProps={{ component: 'div' }}
            helperText={counterHelper(tagsArr.length, 30, 'Press Enter to create a hashtag')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onAskAI} title="Suggest hashtags with AI">
                      <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      <Alert severity="info" icon={<MusicOffIcon fontSize="small" />} sx={{ borderRadius: '4px' }}>
        <Typography variant="caption">
          Music/audio selection is not available via the Instagram Graph API.
          Songs can only be added through the Instagram app after publishing.
        </Typography>
      </Alert>
    </Box>
  );
}

function FacebookFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.facebook;
  const textLen = (customization.text || '').length;
  const tagsArr = Array.isArray(customization.hashtags) ? customization.hashtags : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ContentTypeToggle
        options={[
          { value: 'post', label: 'Text / Photo', icon: <PostIcon fontSize="small" /> },
          { value: 'video', label: 'Video Post', icon: <VideoIcon fontSize="small" /> },
        ]}
        value={customization.content_type}
        onChange={onChange}
      />

      <TextField
        fullWidth
        multiline
        minRows={7}
        maxRows={24}
        label="Post Text"
        placeholder="What's on your mind? Share an update, story, or announcement…"
        value={customization.text || ''}
        onChange={(e) => onChange('text', e.target.value.slice(0, lim.text))}
        error={showErrors && !!errors.facebook_text}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          textLen,
          lim.text,
          showErrors && errors.facebook_text ? errors.facebook_text : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagsArr}
        onChange={(event, newValue) => {
          const newTags = newValue.map(t => {
            let tag = t.trim();
            if (tag && !tag.startsWith('#')) return '#' + tag;
            return tag;
          }).filter(Boolean);
          onChange('hashtags', newTags);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} variant="outlined" label={option} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hashtags"
            placeholder={tagsArr.length === 0 ? "#deals, #community (Press Enter)" : ""}
            FormHelperTextProps={{ component: 'div' }}
            helperText={counterHelper(tagsArr.length, 30, 'Press Enter to create a hashtag')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onAskAI} title="Suggest hashtags with AI">
                      <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

function TwitterFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.twitter;
  const text = customization.text || '';
  const textLen = text.length;
  const remaining = lim.text - textLen;
  const tagsArr = Array.isArray(customization.hashtags) ? customization.hashtags : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <TextField
        fullWidth
        multiline
        minRows={6}
        maxRows={12}
        label="Tweet"
        placeholder="What's happening? Keep it punchy…"
        value={text}
        onChange={(e) => onChange('text', e.target.value.slice(0, lim.text))}
        error={showErrors && (!!errors.twitter_text || remaining < 0)}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          textLen,
          lim.text,
          showErrors && errors.twitter_text ? errors.twitter_text : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagsArr}
        onChange={(event, newValue) => {
          const newTags = newValue.map(t => {
            let tag = t.trim();
            if (tag && !tag.startsWith('#')) return '#' + tag;
            return tag;
          }).filter(Boolean);
          onChange('hashtags', newTags);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} variant="outlined" label={option} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hashtags"
            placeholder={tagsArr.length === 0 ? "#news, #update (Press Enter)" : ""}
            FormHelperTextProps={{ component: 'div' }}
            helperText={counterHelper(tagsArr.length, 10, 'Press Enter to create a hashtag')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onAskAI} title="Suggest hashtags with AI">
                      <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {/* Reply Settings */}
      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
          Who can reply?
        </Typography>
        <ToggleButtonGroup
          value={customization.reply_settings || 'everyone'}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) onChange('reply_settings', val); }}
        >
          <ToggleButton value="everyone" sx={{ px: 2, fontWeight: 600 }}>Everyone</ToggleButton>
          <ToggleButton value="following" sx={{ px: 2, fontWeight: 600 }}>Following</ToggleButton>
          <ToggleButton value="mentionedUsers" sx={{ px: 2, fontWeight: 600 }}>Mentioned Only</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

function LinkedInFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.linkedin;
  const textLen = (customization.text || '').length;
  const tagsArr = Array.isArray(customization.hashtags) ? customization.hashtags : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <TextField
        fullWidth
        multiline
        minRows={8}
        maxRows={24}
        label="Post"
        placeholder="Share professional insights, career updates, industry news, or thought leadership…"
        value={customization.text || ''}
        onChange={(e) => onChange('text', e.target.value.slice(0, lim.text))}
        error={showErrors && !!errors.linkedin_text}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          textLen,
          lim.text,
          showErrors && errors.linkedin_text ? errors.linkedin_text : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={tagsArr}
        onChange={(event, newValue) => {
          const newTags = newValue.map(t => {
            let tag = t.trim();
            if (tag && !tag.startsWith('#')) return '#' + tag;
            return tag;
          }).filter(Boolean);
          onChange('hashtags', newTags);
        }}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });
            return (
              <Chip key={key} variant="outlined" label={option} size="small" {...tagProps} />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Hashtags"
            placeholder={tagsArr.length === 0 ? "#leadership, #innovation (Press Enter)" : ""}
            FormHelperTextProps={{ component: 'div' }}
            helperText={counterHelper(tagsArr.length, 5, 'Press Enter to create a hashtag (LinkedIn recommends max 5)')}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {params.InputProps.endAdornment}
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={onAskAI} title="Suggest hashtags with AI">
                      <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    </IconButton>
                  </InputAdornment>
                </React.Fragment>
              ),
            }}
          />
        )}
      />

      {/* Visibility */}
      <Box>
        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 600 }}>
          Post Visibility
        </Typography>
        <ToggleButtonGroup
          value={customization.visibility || 'PUBLIC'}
          exclusive
          size="small"
          onChange={(_, val) => { if (val) onChange('visibility', val); }}
        >
          <ToggleButton value="PUBLIC" sx={{ px: 2.5, fontWeight: 600 }}>Public (Anyone)</ToggleButton>
          <ToggleButton value="CONNECTIONS" sx={{ px: 2.5, fontWeight: 600 }}>Connections Only</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}

function RedditFields({ customization, onChange, errors, showErrors, onAskAI }) {
  const lim = LIMITS.reddit;
  const subLen = (customization.subreddit || '').length;
  const titleLen = (customization.title || '').length;
  const textLen = (customization.text || '').length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <TextField
        fullWidth
        required
        label="Subreddit"
        placeholder="technology"
        value={customization.subreddit || ''}
        onChange={(e) => onChange('subreddit', e.target.value.slice(0, lim.subreddit).replace(/\s/g, ''))}
        error={showErrors && !!errors.reddit_subreddit}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700 }}>r/</Typography>
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          subLen,
          lim.subreddit,
          showErrors && errors.reddit_subreddit ? errors.reddit_subreddit : 'No spaces — without the r/ prefix',
        )}
      />

      <TextField
        fullWidth
        required
        label="Post Title"
        placeholder="Enter a clear, descriptive title for your Reddit post"
        value={customization.title || ''}
        onChange={(e) => onChange('title', e.target.value.slice(0, lim.title))}
        error={showErrors && !!errors.reddit_title}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(
          titleLen,
          lim.title,
          showErrors && errors.reddit_title ? errors.reddit_title : undefined,
        )}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        fullWidth
        multiline
        minRows={7}
        maxRows={24}
        label="Body Text (optional)"
        placeholder="Add more context, details, or a discussion prompt…"
        value={customization.text || ''}
        onChange={(e) => onChange('text', e.target.value.slice(0, lim.text))}
        FormHelperTextProps={{ component: 'div' }}
        helperText={counterHelper(textLen, lim.text)}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
              <IconButton size="small" onClick={onAskAI} title="Optimize with AI">
                <MagicIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {/* NSFW & Spoiler toggles */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!customization.nsfw}
              onChange={(e) => onChange('nsfw', e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>NSFW</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={!!customization.spoiler}
              onChange={(e) => onChange('spoiler', e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>Spoiler</Typography>}
        />
      </Box>
    </Box>
  );
}

const PLATFORM_FIELDS = {
  youtube: YouTubeFields,
  instagram: InstagramFields,
  facebook: FacebookFields,
  twitter: TwitterFields,
  linkedin: LinkedInFields,
  reddit: RedditFields,
};

// ─── Main accordion ───────────────────────────────────────────────────────────

const PlatformCustomizationPanel = React.memo(({
  platform,
  customization = {},
  onChange,
  errors,
  showErrors,
  expanded,
  onExpandToggle,
  hasError,
  onAskAI,
}) => {
  const config = PLATFORM_CONFIG[platform];
  const icon = PLATFORM_ICONS[platform];

  const handleChange = useCallback(
    (field, value) => onChange(platform, field, value),
    [onChange, platform]
  );

  const PlatformFields = PLATFORM_FIELDS[platform];

  return (
    <Accordion
      expanded={expanded}
      onChange={onExpandToggle}
      sx={{
        mb: 1.5,
        borderRadius: '6px !important',
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: hasError
          ? '1.5px solid rgba(239,68,68,0.5)'
          : expanded
            ? `1.5px solid ${alpha(config.color, 0.4)}`
            : '1px solid rgba(0,0,0,0.1)',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': { boxShadow: expanded ? 'none' : '0 2px 8px rgba(0,0,0,0.06)' },
      }}
    >
      {/* Header */}
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          background: hasError
            ? 'rgba(239,68,68,0.04)'
            : expanded
              ? alpha(config.color, 0.06)
              : alpha(config.color, 0.03),
          borderRadius: expanded ? '6px 6px 0 0' : '6px',
          minHeight: 52,
          transition: 'background 0.2s',
          '&.Mui-expanded': { borderRadius: '6px 6px 0 0' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Avatar sx={{ bgcolor: alpha(config.color, 0.12), color: config.color, width: 30, height: 30 }}>
            {React.cloneElement(icon, { sx: { fontSize: 16 } })}
          </Avatar>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              {config.label}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {config.description}
            </Typography>
          </Box>

          {/* Badges shown in collapsed header */}
          <Box sx={{ ml: 'auto', mr: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            {customization.media_file && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <FileIcon sx={{ fontSize: 13, color: config.color }} />
                <Typography variant="caption" sx={{ color: config.color, fontWeight: 600 }}>
                  {customization.media_file.name.length > 18
                    ? customization.media_file.name.slice(0, 16) + '…'
                    : customization.media_file.name}
                </Typography>
              </Box>
            )}
            {hasError && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ErrorOutlineIcon sx={{ fontSize: 15, color: 'error.main' }} />
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                  Required fields missing
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onAskAI(); }}
            sx={{
              mr: 1,
              color: 'primary.main',
              bgcolor: alpha(config.color, 0.1),
              '&:hover': { bgcolor: alpha(config.color, 0.2) }
            }}
          >
            <MagicIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </AccordionSummary>

      {/* Body */}
      <AccordionDetails sx={{ p: 3 }}>
        {PlatformFields && (
          <PlatformFields
            customization={customization}
            onChange={handleChange}
            errors={errors}
            showErrors={showErrors}
            onAskAI={onAskAI}
          />
        )}

        <Divider sx={{ my: 3 }} />

        <PlatformMediaPicker
          value={customization.media_file || null}
          onChange={(file) => handleChange('media_file', file)}
          accept={config.mediaAccept}
          label={`Media File — ${config.mediaLabel}`}
          error={showErrors ? errors[`${platform}_media`] : null}
        />

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mt: 2 }}>
          {!config.schedulingSupported && (
            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', display: 'block', mb: 1 }}>
              Scheduling is not yet supported for {config.label}. Posts will be published immediately.
            </Typography>
          )}
          <FormGroup sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!customization.isScheduled && !!config.schedulingSupported}
                  disabled={!config.schedulingSupported}
                  onChange={(e) => {
                    handleChange('isScheduled', e.target.checked);
                    if (e.target.checked && !customization.scheduledDate) {
                      handleChange('scheduledDate', dayjs().add(5, 'minute'));
                    }
                  }}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: !config.schedulingSupported ? 'text.disabled' : 'inherit' }}>
                  Schedule for later
                </Typography>
              }
            />
          </FormGroup>

          {customization.isScheduled && (
            <Box sx={{ mb: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Publish Date & Time"
                  value={customization.scheduledDate || dayjs().add(5, 'minute')}
                  onChange={(newValue) => handleChange('scheduledDate', newValue)}
                  minDateTime={dayjs().add(5, 'minute')}
                  slotProps={{ textField: { size: 'small' } }}
                  sx={{ width: { xs: '100%', sm: '300px' } }}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Time zone: {dayjs.tz?.guess() || Intl.DateTimeFormat().resolvedOptions().timeZone}
              </Typography>
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
});

export default PlatformCustomizationPanel;
